export const LOAD_FILE_DATE_LOCALES = ['en-GB', 'en-US'] as const;
export type LoadFileDateLocale = (typeof LOAD_FILE_DATE_LOCALES)[number];

export const DEFAULT_LOAD_FILE_DATE_LOCALE: LoadFileDateLocale = 'en-GB';

export interface LoadFileDateLocaleAnalysis {
  /** Suggested locale when the sample contains evidence for only one locale. */
  suggestedLocale: LoadFileDateLocale | null;
  enGBOnlyCount: number;
  enUSOnlyCount: number;
  enGBOnlyExample: string | null;
  enUSOnlyExample: string | null;
}

/**
 * Parse the date/time shape emitted by Relativity load-file exports.
 *
 * Date-only values are deliberately rejected: Relativity exports those from
 * text fields as well, while actual Date fields include a time component.
 * Civil date/time values are stored as UTC so imports are deterministic across
 * workers and environments.
 */
export function parseLoadFileDate(value: string, locale: LoadFileDateLocale): Date | null {
  const match =
    /^(\d{2})\/(\d{2})\/(\d{4})[ \t]+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:[ \t]+([ap]m))?$/iu.exec(
      value.trim(),
    );
  if (match === null) return null;

  const [, first, second, yearText, hourText, minuteText, secondText, meridiem] =
    match as unknown as [string, string, string, string, string, string, string?, string?];
  const day = Number(locale === 'en-GB' ? first : second);
  const month = Number(locale === 'en-GB' ? second : first);
  const year = Number(yearText);
  let hour = Number(hourText);
  const minute = Number(minuteText);
  const secondValue = secondText === undefined ? 0 : Number(secondText);

  if (month < 1 || month > 12 || day < 1 || day > 31 || minute > 59 || secondValue > 59) {
    return null;
  }

  if (meridiem === undefined) {
    if (hour > 23) return null;
  } else {
    if (hour < 1 || hour > 12) return null;
    hour %= 12;
    if (meridiem.toLowerCase() === 'pm') hour += 12;
  }

  const timestamp = Date.UTC(year, month - 1, day, hour, minute, secondValue);
  const date = new Date(timestamp);
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day ||
    date.getUTCHours() !== hour ||
    date.getUTCMinutes() !== minute ||
    date.getUTCSeconds() !== secondValue
  ) {
    return null;
  }

  return date;
}

export function normalizeLoadFileDate(value: string, locale: LoadFileDateLocale): string | null {
  return parseLoadFileDate(value, locale)?.toISOString() ?? null;
}

/**
 * Find unambiguous locale evidence across sampled cells. Values valid under
 * both locales (for example 05/12) do not influence the suggestion.
 */
export function analyseLoadFileDateLocale(values: Iterable<string>): LoadFileDateLocaleAnalysis {
  let enGBOnlyCount = 0;
  let enUSOnlyCount = 0;
  let enGBOnlyExample: string | null = null;
  let enUSOnlyExample: string | null = null;

  for (const value of values) {
    const validForEnGB = parseLoadFileDate(value, 'en-GB') !== null;
    const validForEnUS = parseLoadFileDate(value, 'en-US') !== null;

    if (validForEnGB && !validForEnUS) {
      enGBOnlyCount += 1;
      enGBOnlyExample ??= value.trim();
    } else if (validForEnUS && !validForEnGB) {
      enUSOnlyCount += 1;
      enUSOnlyExample ??= value.trim();
    }
  }

  const suggestedLocale =
    enGBOnlyCount > 0 && enUSOnlyCount === 0
      ? 'en-GB'
      : enUSOnlyCount > 0 && enGBOnlyCount === 0
        ? 'en-US'
        : null;

  return {
    suggestedLocale,
    enGBOnlyCount,
    enUSOnlyCount,
    enGBOnlyExample,
    enUSOnlyExample,
  };
}
